import SearchInput from "@components/input/SearchInput";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { Accordion, AccordionItem } from "@szhsin/react-accordion";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import CategoryDropdown from "./_components/CategoryDropdown";
import ProductStautsDropdown from "./_components/ProductStutsDropdown";
import StockDropdown from "./_components/StockDropdown";
import BasicButton from "@components/button/BasicButton";
import { useLocation, useNavigate } from "react-router-dom";

type SelectedCategory = {
  categoryCode: number; // 선택한 카테고리의 실제 코드
  depth: number; // 선택한 뎁스 (1~4)
  path: string[]; // 경로명 배열 ['여성', '의류', '티셔츠', '반팔']
  codes: number[]; // 경로 코드 배열 ['W001', 'WC001', 'WCT001', 'WCTS001']
};

const ProductSearch = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { from } = location.state || {};

  // 1. States
  const [inputValue, setInputValue] = useState("");
  const [minStockValue, setMinStockValue] = useState("");
  const [maxStockValue, setMaxStockValue] = useState("");
  const [selectedProductStatuses, setSelectedProductStatuses] = useState<
    string[]
  >([]);

  const [selectedCategories, setSelectedCategories] = useState<
    SelectedCategory[]
  >([]);

  const [isOpen, setIsOpen] = useState({
    status: false,
    stock: false,
    category: false,
    productSearch: false,
  });

  const statusLabelMap: Record<string, string> = {
    TEMP: "임시저장",
    STOCK: "재고",
    SALE: "판매",
  };

  // 2. Queries

  // 3. Effects

  // 4. Functions

  const handleToggle = (section: keyof typeof isOpen) => {
    setIsOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // 검색
  const handleSearch = () => {
    // 중복 제거
    const allCodes = selectedCategories
      .flatMap((c) => c.codes) // 모든 codes 배열을 하나로 합침
      .filter((code, index, array) => array.indexOf(code) === index); // 중복 제거

    const pathStrings = selectedCategories.map((c) =>
      encodeURIComponent(c.path.join(">")),
    );
    const query = new URLSearchParams({
      product_name: inputValue,
      status: selectedProductStatuses.join(","),
      stock_from: minStockValue,
      stock_to: maxStockValue,
      category: allCodes.join(","),
      category_paths: pathStrings.join("|"), // 구분자는 | 로
      status_labels: selectedProductStatuses
        .map((s) => statusLabelMap[s])
        .join(","), // 한글값
    });

    navigate(
      from === "register"
        ? `/register-list?${query.toString()}`
        : `/capture-list?${query.toString()}`,
    );
  };

  // 필터 선택값 제거
  const handleRemoveFilter = (
    type: "category" | "status" | "stock",
    value?: any,
  ) => {
    switch (type) {
      case "category":
        if (value) {
          setSelectedCategories((prev) =>
            prev.filter((category) => category.categoryCode !== value),
          );
        } else {
          setSelectedCategories([]);
        }
        break;
      case "status":
        if (value === "TEMP,STOCK,SALE") {
          setSelectedProductStatuses([]);
        } else {
          setSelectedProductStatuses((prev) =>
            prev.filter((v) => !value?.split(",").includes(v)),
          );
        }
        break;
      case "stock":
        setMinStockValue("");
        setMaxStockValue("");
        break;
    }
  };

  // 필터 초기화
  const resetFilters = () => {
    // 카테고리 필터 초기화
    setSelectedCategories([]);
    // 상품 상태 필터 초기화
    setSelectedProductStatuses([]);

    // 재고 수량 필터 초기화
    setMinStockValue("");
    setMaxStockValue("");

    // 검색어 초기화
    setInputValue("");
  };

  // 상품 상태 필터 표시
  const getDisplayStatuses = (statuses: string[]): string[] => {
    return statuses
      .filter((s) => ["TEMP", "STOCK", "SALE"].includes(s))
      .map((s) => (s === "TEMP" ? "임시저장" : s));
  };

  return (
    <Container>
      {(selectedCategories.length > 0 ||
        getDisplayStatuses(selectedProductStatuses).length > 0 ||
        inputValue ||
        minStockValue ||
        maxStockValue) && (
        <FilterDisplay>
          <FilterList>
            {/* 입력한 상품명 */}
            {inputValue && (
              <FilterItem>
                {inputValue}
                <DeleteButton onClick={() => setInputValue("")}>
                  <Image
                    src={theme.image.close.default}
                    height="1.8rem"
                    width="1.8rem"
                  />
                </DeleteButton>
              </FilterItem>
            )}

            {/* 선택된 카테고리 */}
            {selectedCategories.length > 0 &&
              selectedCategories.map((filter) => (
                <FilterItem key={filter.categoryCode}>
                  {filter.path.join(" > ")}
                  <DeleteButton
                    onClick={() =>
                      handleRemoveFilter("category", filter.categoryCode)
                    }>
                    <Image
                      src={theme.image.close.default}
                      height="1.8rem"
                      width="1.8rem"
                    />
                  </DeleteButton>
                </FilterItem>
              ))}

            {/* 선택된 상품 상태 */}
            {selectedProductStatuses.map((status) => (
              <FilterItem key={status}>
                {status === "TEMP"
                  ? "임시저장"
                  : status === "STOCK"
                    ? "재고"
                    : status === "SALE"
                      ? "판매"
                      : status}

                <DeleteButton
                  onClick={() => handleRemoveFilter("status", status)}>
                  <Image
                    src={theme.image.close.default}
                    height="1.8rem"
                    width="1.8rem"
                  />
                </DeleteButton>
              </FilterItem>
            ))}

            {/* 재고 수량 */}
            {(minStockValue || maxStockValue) && (
              <FilterItem>
                {minStockValue && !maxStockValue
                  ? `${minStockValue} ~`
                  : !minStockValue && maxStockValue
                    ? `~ ${maxStockValue}`
                    : minStockValue && maxStockValue
                      ? `${minStockValue} ~ ${maxStockValue}`
                      : null}

                <DeleteButton onClick={() => handleRemoveFilter("stock")}>
                  <Image
                    src={theme.image.close.default}
                    height="1.8rem"
                    width="1.8rem"
                  />
                </DeleteButton>
              </FilterItem>
            )}
          </FilterList>
        </FilterDisplay>
      )}

      {/* 상품 명  */}
      <SearchInput
        placeholder="상품명 검색"
        inputValue={inputValue}
        setInputValue={setInputValue}
        backgroundColor="white"
      />

      {/* 상품상태 아코디언 */}
      <StyledAccordion>
        <AccordionItem
          className="status"
          itemKey="status"
          header={
            <CategoryHeader onClick={() => handleToggle("status")}>
              상품등록
              <ArrowButton>
                <Image
                  src={
                    isOpen.status
                      ? theme.image.arrow_down.default
                      : theme.image.arrow_up.default
                  }
                />
              </ArrowButton>
            </CategoryHeader>
          }>
          <ProductStautsDropdown
            selectedStatuses={selectedProductStatuses}
            setSelectedStatuses={setSelectedProductStatuses}
          />
        </AccordionItem>
      </StyledAccordion>
      {/* 재고수량 아코디언 */}
      <StyledAccordion>
        <AccordionItem
          className="stock"
          itemKey="stock"
          header={
            <CategoryHeader onClick={() => handleToggle("stock")}>
              재고수량
              <ArrowButton>
                <Image
                  src={
                    isOpen.stock
                      ? theme.image.arrow_down.default
                      : theme.image.arrow_up.default
                  }
                />
              </ArrowButton>
            </CategoryHeader>
          }>
          <StockDropdown
            minValue={minStockValue}
            maxValue={maxStockValue}
            setMinValue={setMinStockValue}
            setMaxValue={setMaxStockValue}
          />
        </AccordionItem>
      </StyledAccordion>
      {/* 카테고리 아코디언 */}
      <StyledAccordion>
        <AccordionItem
          className="category"
          itemKey="category"
          header={
            <CategoryHeader onClick={() => handleToggle("category")}>
              카테고리
              <ArrowButton>
                <Image
                  src={
                    isOpen.category
                      ? theme.image.arrow_down.default
                      : theme.image.arrow_up.default
                  }
                />
              </ArrowButton>
            </CategoryHeader>
          }>
          <CategoryDropdown
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
          />
        </AccordionItem>
      </StyledAccordion>
      {/* 초가화 버튼 */}
      <ResetButton onClick={resetFilters}>
        <Image
          src={theme.image.reset_icon.default}
          height="1.8rem"
          width="1.8rem"
        />
        초기화
      </ResetButton>
      {/* 검색 버튼 */}
      <ButtonWrapper>
        <BasicButton
          title="검색"
          height="5.2rem"
          width="100%"
          onClick={handleSearch}
        />
      </ButtonWrapper>
    </Container>
  );
};

export default ProductSearch;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  padding: "0 2rem",
  gap: "1rem",
  minHeight: "100dvh",
  position: "relative",
  paddingBottom: "20rem", // 버튼 높이만큼 하단 패딩 추가
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  button: { width: "100%" },

  ".two": {
    paddingLeft: "2rem",
  },
  ".three": {
    paddingLeft: "2rem",
  },
  ".four": {
    paddingLeft: "4rem",
  },
  ".szh-accordion__item-content": {
    transition: "height 0.25s cubic-bezier(0, 0, 0, 1)",
  },
}));

const CategoryHeader = styled.div(({ theme }) => ({
  backgroundColor: theme.color.gray_02,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "1.5rem",
  fontSize: "1.6rem",
  borderRadius: "10px",
  fontWeight: theme.fontWeight.medium,
}));

const ArrowButton = styled.div({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ButtonWrapper = styled.div({
  position: "fixed",
  bottom: "-0.4rem",
  left: "50%",
  transform: "translateX(-50%)",
  width: "100%",
  maxWidth: "56rem",
  padding: "2rem",
  zIndex: 10,
});

// 필터 표시 관련 스타일
const FilterDisplay = styled.div({
  display: "flex",
  alignItems: "center",
  marginTop: "2rem",
});

const FilterList = styled.div({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "0.5rem",
});

const FilterItem = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "0.5rem 1rem",
  borderRadius: "1rem",
  border: `1px solid ${theme.color.light_gray}`,
  gap: "0.5rem",
  whiteSpace: "nowrap",
}));

const DeleteButton = styled.button({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
});

const ResetButton = styled.button(({ theme }) => ({
  position: "absolute",
  bottom: "8rem",
  right: "2rem",
  color: theme.color.light_gray,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  fontSize: "1.2rem",
  cursor: "pointer",
}));
